// Connected OAuth apps (remote MCP connectors like claude.ai).
// Lists clients the user authorized via /oauth/consent and lets them revoke
// all tokens for a client in one click.
import { useState, useEffect } from 'react';
import { Link2, Trash2 } from 'lucide-react';
import { apiFetch } from '../services/apiClient.js';
import styles from './ConnectedApps.module.css';

export default function ConnectedApps() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revokingId, setRevokingId] = useState(null);

  useEffect(() => {
    loadApps();
  }, []);

  async function loadApps() {
    try {
      const data = await apiFetch('/v1/me/connected-apps');
      setApps(data?.connected_apps || []);
    } catch (error) {
      console.error('Error loading connected apps:', error);
    } finally {
      setLoading(false);
    }
  }

  async function revokeApp(clientId, clientName) {
    if (!confirm(`Disconnect "${clientName || clientId}"? It will lose access to your account immediately.`)) return;
    setRevokingId(clientId);
    try {
      await apiFetch(`/v1/me/connected-apps?client_id=${encodeURIComponent(clientId)}`, { method: 'DELETE' });
      setApps((prev) => prev.filter((app) => app.client_id !== clientId));
    } catch (error) {
      console.error('Error revoking connected app:', error);
      alert('Failed to disconnect the application');
    } finally {
      setRevokingId(null);
    }
  }

  if (loading || apps.length === 0) return null;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>
        <Link2 size={20} aria-hidden="true" />
        Connected Apps
      </h2>
      <p className={styles.sectionSubtitle}>
        Applications you authorized to access your account via OAuth (e.g. the
        Claude connector).
      </p>
      <div className={styles.list}>
        {apps.map((app) => (
          <div key={app.client_id} className={styles.row}>
            <div className={styles.rowInfo}>
              <span className={styles.appName}>{app.client_name || 'Unnamed application'}</span>
              <span className={styles.appMeta}>
                Connected {new Date(app.connected_at).toLocaleDateString()}
                {app.last_used_at && ` · Last used ${new Date(app.last_used_at).toLocaleDateString()}`}
              </span>
            </div>
            <button
              type="button"
              className={styles.revokeBtn}
              onClick={() => revokeApp(app.client_id, app.client_name)}
              disabled={revokingId === app.client_id}
            >
              <Trash2 size={16} aria-hidden="true" />
              {revokingId === app.client_id ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
